import { t, type Language, type StringKey } from "../lib/i18n";

interface ErrorPanelProps {
  titleKey: StringKey;
  bodyKey: StringKey;
  language: Language;
  onRetry?: (() => void) | undefined;
  retryLabelKey?: StringKey;
  /** Optional technical reason (e.g. a Teams SSO failure code) rendered
   * as a secondary line so an operator can diagnose without DevTools. */
  detail?: string | undefined;
  detailLabelKey?: StringKey;
}

/** Generic error surface — used for session-expired, unknown-tenant,
 * and fetch-failed cases. The Tab layout reserves space for it so a
 * retry doesn't reflow the whole page. */
export function ErrorPanel({
  titleKey,
  bodyKey,
  language,
  onRetry,
  retryLabelKey = "error.retry",
  detail,
  detailLabelKey = "error.detail_label",
}: ErrorPanelProps): JSX.Element {
  return (
    <div
      role="alert"
      className="rounded-[2px] border border-kwanza-alert/20 bg-kwanza-card p-6 shadow-sm"
      data-testid="error-panel"
    >
      <h2 className="text-lg font-semibold text-kwanza-alert">
        {t(titleKey, language)}
      </h2>
      <p className="mt-2 text-sm text-kwanza-ink">{t(bodyKey, language)}</p>
      {detail ? (
        <p
          className="mt-3 break-words font-mono text-xs text-kwanza-inkSoft"
          data-testid="error-detail"
        >
          {t(detailLabelKey, language)}: {detail}
        </p>
      ) : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          data-testid="error-retry"
          className="mt-4 rounded-md bg-kwanza-accent px-3 py-1.5 text-sm font-bold text-kwanza-onAccent hover:bg-kwanza-accentHover"
        >
          {t(retryLabelKey, language)}
        </button>
      ) : null}
    </div>
  );
}
